import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/outline';
import { updateContractStatus } from '../services/contractService';

const OTPVerification: React.FC = () => {
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { contractId } = useParams<{ contractId: string }>();
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action') as 'approve' | 'reject';
  
  // Redirect if action is invalid
  useEffect(() => {
    if (!contractId || !action || !['approve', 'reject'].includes(action)) {
      navigate('/contracts');
    }
  }, [contractId, action, navigate]);
  
  const handleStatusUpdate = async (status: 'نشط' | 'مرفوض') => {
    if (!contractId) return false;
    
    try {
      await updateContractStatus(contractId, status);
      return true;
    } catch (error) {
      console.error('Error updating contract status:', error);
      setError('حدث خطأ أثناء تحديث حالة العقد');
      return false;
    }
  };
  
  const handleVerify = async () => {
    if (!contractId || !action) return false;
    
    const newStatus = action === 'approve' ? 'نشط' : 'مرفوض';
    return handleStatusUpdate(newStatus);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 4) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const success = await handleVerify();
      if (success) {
        navigate(`/contracts/${contractId}`, {
          state: { 
            success: true, 
            message: `تم ${action === 'approve' ? 'الموافقة على العقد' : 'رفض العقد'} بنجاح` 
          }
        });
      }
    } catch (error) {
      console.error('Error:', error);
      setError('حدث خطأ، يرجى المحاولة مرة أخرى');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleBack = () => {
    navigate(`/contracts/${contractId}`);
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen font-tajawal">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button 
          onClick={handleBack}
          className="p-2 rounded-full hover:bg-gray-200"
        >
          <ChevronLeftIcon className="h-6 w-6 text-gray-700 transform rotate-180" />
        </button>
        <h2 className="text-2xl font-bold text-gray-900 mr-4">
          {action === 'approve' ? 'تأكيد الموافقة على العقد' : 'تأكيد رفض العقد'}
        </h2>
      </div>

      <div className="bg-white rounded-3xl p-6 shadow-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            أدخل رمز التحقق
          </h3>
          <p className="text-gray-500 text-sm">
            تم إرسال رمز مكون من 4 أرقام إلى هاتفك
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-8">
            <div className="flex justify-center gap-3 mb-2">
              {[0, 1, 2, 3].map((i) => (
                <input
                  key={i}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={otp[i] || ''}
                  onChange={(e) => {
                    const newOtp = otp.split('');
                    newOtp[i] = e.target.value.replace(/[^0-9]/g, '');
                    setOtp(newOtp.join('').substring(0, 4));
                  }}
                  className="w-16 h-16 text-center text-2xl font-medium border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                  autoFocus={i === 0}
                />
              ))}
            </div>
            {error && (
              <p className="text-center text-sm text-red-500 mt-2">
                {error}
              </p>
            )}
            <p className="text-center text-sm text-gray-500 mt-2">
              لم تستلم الرمز؟ <button type="button" className="text-blue-600 font-medium">إعادة إرسال</button>
            </p>
          </div>

          <button
            type="submit"
            disabled={otp.length !== 4 || isLoading}
            className={`w-full py-3 px-4 rounded-xl text-white font-medium text-center ${
              otp.length === 4 && !isLoading ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300'
            } transition-colors`}
          >
            {isLoading ? 'جاري التحقق...' : 'تأكيد'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default OTPVerification;
